import React, { useContext, useEffect, useState } from 'react';
import { Alert, Box, CircularProgress, FormControl, InputLabel, MenuItem, Select, Stack, Typography } from '@mui/material';
import { ModalDialog } from 'components';
import { CallFilledIcon } from 'components/mui';
import { snackbarService } from 'components/mui/snackbar/snackbar-service';
import { UserStateContext } from 'context/user-state-context';
import { SoftphoneContext } from 'context/softphone-context';
import utils from 'shared/utils';
import { createCinchPhonesCall, getCinchPhonesCallers } from 'shared/cinch-phones.api';

/**
 * Confirm a click-to-dial call to a customer.
 *
 * These are business phones rather than personal ones, so the caller picks which phone to
 * ring. That phone rings first and only reaches the customer once someone answers it —
 * worth a confirmation step, since it's surprising otherwise and costs a real call.
 *
 * Stacks above the customer drawer, which is where this is opened from. Modal z-index is
 * 900 + 100 * level, and the drawer's own modals all use 10 — anything lower renders
 * underneath the drawer's backdrop.
 */
// The value of the "call from this browser" option. Not a phone id, so it can't collide
// with one.
const SOFTPHONE = 'softphone';
export const CallModal = ({
  open,
  onClose,
  onSubmit,
  phone,
  name,
  level = 10
}) => {
  const {
    asCompany
  } = useContext(UserStateContext);
  const softphone = useContext(SoftphoneContext) || {};
  const [phones, setPhones] = useState([]);
  const [selected, setSelected] = useState('');
  const [loading, setLoading] = useState(true);
  const [calling, setCalling] = useState(false);
  useEffect(() => {
    if (!asCompany?.id) return;
    let cancelled = false;
    setLoading(true);
    getCinchPhonesCallers({
      company: asCompany.id
    }).then(({
      data
    }) => {
      if (cancelled) return;
      const results = data?.results || [];
      setPhones(results);

      // Calling from the browser you're already sitting in is the least disruptive
      // option, so it wins when the softphone is connected. Otherwise preselect a lone
      // phone, keeping the common case a single click.
      if (softphone.connected) {
        setSelected(SOFTPHONE);
      } else if (results.length === 1) {
        setSelected(results[0].id);
      }
    }).catch(err => {
      console.error('Error loading Cinch Phones callers:', err);
    }).finally(() => {
      if (!cancelled) setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [asCompany?.id, softphone.connected]);
  const handleSubmit = () => {
    setCalling(true);
    if (selected === SOFTPHONE) {
      // Dialled by this browser rather than the server, so the call appears in the
      // softphone panel - opened here, since otherwise the call would be invisible.
      softphone.placeCall(phone).then(() => {
        softphone.open();
        onClose();
      }).catch(error => {
        console.error('Error placing softphone call:', error);
        snackbarService.popup({
          type: 'error',
          message: 'Unable to place that call from this browser.'
        });
      }).finally(() => setCalling(false));
      return;
    }
    createCinchPhonesCall({
      company: asCompany.id,
      user: selected,
      dial_string: phone
    }).then(({
      data
    }) => {
      if (onSubmit) {
        onSubmit(data);
      }
      snackbarService.popup({
        type: 'success',
        message: data?.detail || 'Calling. Answer the phone to connect.'
      });
      onClose();
    }).catch(error => {
      console.error('Error placing Cinch Phones call:', error);
      const detail = error?.response?.data?.detail;
      snackbarService.popup({
        type: 'error',
        message: Array.isArray(detail) ? detail[0] : detail || 'Unable to place that call. Please try again.'
      });
    }).finally(() => setCalling(false));
  };
  const label = option => option.location_name ? `${option.name} — ${option.location_name}` : option.name;
  return <ModalDialog size="sm" level={level} open={open} title="Call Customer" cancelText="Cancel" onCancel={onClose} onSubmit={handleSubmit} submitText={calling ? 'Calling…' : 'Call'} submitDisabled={calling || loading || !selected} allowBackdropClick>
      <Stack spacing={2} sx={{
      marginTop: '1rem'
    }}>
        <Box sx={{
        display: 'flex',
        alignItems: 'center'
      }}>
          <CallFilledIcon />
          <Typography sx={{
          pl: '10px',
          fontSize: '18px'
        }}>{utils.formatPhoneNumber(phone) || phone}</Typography>
        </Box>

        {name && <Typography sx={{
        color: '#374051'
      }}>{name}</Typography>}

        {loading && <Box sx={{
        display: 'flex',
        justifyContent: 'center',
        py: 2
      }}>
            <CircularProgress size={20} />
          </Box>}

        {!loading && phones.length === 0 && !softphone.connected && <Alert severity="warning">
            You don't have access to any phones on this account. Ask an admin to add one in Cinch Phones, or to set the location on an
            existing phone.
          </Alert>}

        {!loading && (phones.length > 0 || softphone.connected) && <>
            <FormControl fullWidth>
              <InputLabel id="cinch-phones-caller-label">Call from</InputLabel>
              <Select labelId="cinch-phones-caller-label" label="Call from" value={selected} disabled={calling} onChange={event => setSelected(event.target.value)}
          // The menu portals to the body, where MUI's default z-index (1300) puts it
          // behind this modal. Derived from the same formula the modal uses for its
          // own content (900 + 100 * level) so it stays above whatever level is set.
          MenuProps={{
            sx: {
              zIndex: 1000 + 100 * level
            }
          }}>
                {softphone.connected && <MenuItem value={SOFTPHONE}>This browser{softphone.phone?.name ? ` — ${softphone.phone.name}` : ''}</MenuItem>}

                {phones.map(option => <MenuItem key={option.id} value={option.id}>
                    {label(option)}
                  </MenuItem>)}
              </Select>
            </FormControl>

            <Alert severity="info">
              {selected === SOFTPHONE ? 'The call is placed from this browser. Keep the softphone open to talk.' : "That phone will ring first. When it's answered, we'll connect it to this number."}
            </Alert>
          </>}
      </Stack>
    </ModalDialog>;
};
export default CallModal;